/**
 * ParameterChips component for selecting parameters in the FAB data entry modal.
 *
 * Displays a row of selectable chips, one for each parameter.
 * Uses short labels for common parameters to save horizontal space.
 */
import { Paper, Chip, Group } from '@mantine/core';

/**
 * Minimal parameter info required for chip display.
 * Works with both ParameterInfo and ManualEntryParameterInfo types.
 */
interface ChipParameter {
  key: string;
  display_name: string;
}

/**
 * Short labels for common parameters to save horizontal space.
 * Keys must match the parameters table (e.g., 'ca' not 'calcium').
 * Falls back to display_name if not found here.
 */
const CHIP_LABELS: Record<string, string> = {
  ph: 'pH',
  salinity: 'Sal',
  alkalinity: 'Alk',
  ca: 'Ca',
  mg: 'Mg',
  no3: 'NO₃',
  po4: 'PO₄',
};

export interface ParameterChipsProps {
  /** List of parameters to display as chips */
  parameters: ChipParameter[];
  /** Currently selected parameter key, or null if none selected */
  selected: string | null;
  /** Callback when a chip is clicked */
  onChange: (parameterKey: string) => void;
}

/**
 * Get the display label for a parameter.
 * Uses short labels from CHIP_LABELS if available, otherwise falls back to display_name.
 */
function getChipLabel(param: ChipParameter): string {
  return CHIP_LABELS[param.key] ?? param.display_name;
}

export function ParameterChips({
  parameters,
  selected,
  onChange,
}: ParameterChipsProps) {
  return (
    <Paper p="xs">
      <Chip.Group
        value={selected ?? undefined}
        onChange={(value) => {
          if (value) {
            onChange(value as string);
          }
        }}
      >
        <Group gap="xs">
          {parameters.map((param) => (
            <Chip key={param.key} value={param.key} size="sm">
              {getChipLabel(param)}
            </Chip>
          ))}
        </Group>
      </Chip.Group>
    </Paper>
  );
}
